import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

import { formatarData } from '@/nucleo/formato/datas';

import { rotuloDosTipos } from '../catalogo-veiculos/rotulos';
import { CamposDoCadastro } from './CamposDoCadastro';
import type { LinhaDoCadastro } from './cadastros.contrato';
import { mensagemDoErro, useAlterarCadastro, useCriarCadastro } from './cadastros.dados';
import type { DefinicaoDoCadastro } from './cadastros.definicoes';
import { useFormularioDoCadastro } from './useFormularioDoCadastro';

interface Props {
  definicao: DefinicaoDoCadastro;
  /** Sem registro, cria; com registro, altera. */
  registro?: LinhaDoCadastro;
  aoFechar: () => void;
}

const PREFIXO = 'cadastro';

/**
 * Janela de criar e de editar um cadastro de domínio. Grava de verdade: a
 * resposta da API (nome repetido, versão desatualizada) aparece na própria
 * janela, que só fecha quando o registro foi salvo.
 */
export function DialogoDeCadastro({ definicao, registro, aoFechar }: Props) {
  const criar = useCriarCadastro(definicao.grupo, definicao.chave);
  const alterar = useAlterarCadastro(definicao.grupo, definicao.chave);
  const formulario = useFormularioDoCadastro(definicao, registro);
  const [recusa, setRecusa] = useState<string | null>(null);
  const enviando = criar.isPending || alterar.isPending;

  const nova = definicao.genero === 'a';
  const entidade = definicao.nomeDaEntidade.toLowerCase();
  const titulo = registro ? `Editar ${entidade}` : `${nova ? 'Nova' : 'Novo'} ${entidade}`;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setRecusa(null);
    const entrada = formulario.conferir(PREFIXO);
    if (!entrada) return;

    const aoSalvar = {
      onSuccess: () => {
        toast.success(`${definicao.nomeDaEntidade} ${nova ? 'salva' : 'salvo'}.`);
        aoFechar();
      },
      onError: (erro: unknown) =>
        setRecusa(mensagemDoErro(erro, 'Não foi possível salvar. Tente de novo em instantes.')),
    };
    if (registro) alterar.mutate({ id: registro.id, entrada }, aoSalvar);
    else criar.mutate(entrada, aoSalvar);
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && aoFechar()}
      title={titulo}
      description={
        registro
          ? 'A alteração vale para todo o sistema assim que for salva.'
          : `Preencha os dados ${nova ? 'da nova' : 'do novo'} ${entidade}.`
      }
      desktopMaxWidth="max-w-xl"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button type="submit" form="formulario-cadastro" disabled={enviando} aria-busy={enviando}>
            {enviando ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      }
    >
      <form id="formulario-cadastro" noValidate onSubmit={enviar} className="space-y-6">
        {recusa && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{recusa}</AlertDescription>
          </Alert>
        )}
        {definicao.integracaoComFonte && registro && <OQueAFonteDiz registro={registro} />}
        <CamposDoCadastro
          campos={definicao.campos}
          valores={formulario.valores}
          problemas={formulario.problemas}
          aoAlterar={formulario.alterar}
          prefixo={PREFIXO}
          duasColunas={false}
        />
      </form>
    </ResponsiveModal>
  );
}

const ORIGEM_DA_ALTERACAO: Readonly<Record<string, string>> = {
  PESSOA: 'Pela tela, por uma pessoa',
  CARGA_AUTOMATICA: 'Pela consulta automática à fonte oficial',
  MIGRACAO: 'Pela carga inicial do sistema',
};

/**
 * O que a fonte oficial diz do item e quem o alterou por último, para quem
 * vai editar saber se a consulta diária ainda cuida dele. Só leitura: o
 * histórico completo, com valor anterior e novo, está na trilha de auditoria.
 */
function OQueAFonteDiz({ registro }: { registro: LinhaDoCadastro }) {
  const visto = typeof registro.vistoNaFonteEm === 'string' ? registro.vistoNaFonteEm : null;
  const naoVisto =
    typeof registro.naoVistoNaFonteDesde === 'string' ? registro.naoVistoNaFonteDesde : null;
  const origem =
    typeof registro.alteradoPorOrigem === 'string'
      ? (ORIGEM_DA_ALTERACAO[registro.alteradoPorOrigem] ?? registro.alteradoPorOrigem)
      : 'Antes do registro de origem';
  const sugestao = typeof registro.sugestaoDaFonte === 'string' ? registro.sugestaoDaFonte : null;

  return (
    <section
      aria-label="O que a fonte oficial diz deste item"
      className="space-y-2 rounded-md border bg-muted/40 p-3 text-sm"
    >
      <dl className="grid gap-2 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted-foreground">Na fonte oficial</dt>
          <dd>
            {naoVisto
              ? `Não visto desde ${formatarData(naoVisto)}`
              : visto
                ? `Visto em ${formatarData(visto)}`
                : 'Ainda sem texto da fonte reconhecido'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Última alteração</dt>
          <dd>{origem}</dd>
        </div>
        {typeof registro.alteradoPelaFonte === 'string' && (
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">Arquivo da fonte</dt>
            <dd>{registro.alteradoPelaFonte}</dd>
          </div>
        )}
      </dl>
      {sugestao && (
        <p className="text-sm">
          A fonte sugere o tipo <strong>{rotuloDosTipos(sugestao)}</strong>. O item está protegido,
          então nada mudou sozinho: para aceitar, escreva o tipo abaixo e salve.
        </p>
      )}
    </section>
  );
}
